import React, { useEffect, useState } from 'react';
import { Localizer } from 'services/Localizer';
import { getUserData } from 'services/UserData';
import Spinner from 'shared/components/Spinner';
import InlineAlert from 'shared/edit/InlineAlert';
import { TextBoxField, DropdownField } from 'shared/edit/fields';
import ReCaptchaDisclaimer from 'shared/edit/fields/ReCaptchaDisclaimer';
import { useAxiosFormSubmitter } from 'shared/hooks/useBaseAxiosFormSubmitter';
import RocForm from 'shared/edit/RocForm';
import { CareersPageData, JobApplicationFormValues } from './types';
import { DropdownOption } from '../../../../Roc.SharedClientApp/types/fields';
import FileUpload from 'features/file-upload/FileUpload';

function getCareersData(): CareersPageData {
	return (window as any).__TAPECASE_CAREERS_PAGE_DATA__ ?? {};
}

/**
 *  Job Application Form component
 */
function JobApplicationForm() {
	const { jobPositions } = getCareersData();

	const jobPositionsOptions = jobPositions.map((o) => ({ text: o.name, value: o.value } as DropdownOption));

	const [initialValues, setInitialValues] = useState<JobApplicationFormValues>({
		firstName: '',
		lastName: '',
		email: '',
		phone: '',
		jobPositionId: '',
		uploadedFileInfo: null,
	});

	const jobApplicationFormSubmitter = useAxiosFormSubmitter<JobApplicationFormValues>(
		{
			url: '/ajax/careers',
		},
		'JobApplicationForm',
	);

	// initialize the job application fields with values from the user record.
	useEffect(() => {
		(async () => {
			const userDataResponse = await getUserData();

			if (!userDataResponse.data || !userDataResponse.data.summary) {
				return;
			}

			const { firstName, lastName, email, phoneNumber } = userDataResponse.data.summary;

			setInitialValues({
				...initialValues,
				firstName: firstName || '',
				lastName: lastName || '',
				email: email || '',
				phone: phoneNumber || '',
			});
		})();
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, []);

	return !jobApplicationFormSubmitter.submitted ? (
		<RocForm<JobApplicationFormValues>
			onSubmit={jobApplicationFormSubmitter.handleSubmit}
			initialValues={initialValues}
			render={({ handleSubmit, submitError, submitting, form }) => {
				return (
					<form onSubmit={handleSubmit} className="roc-carrers-form">
						<div className="roc-checkout__section">
							{submitError ? <InlineAlert message={submitError} error={null} /> : null}
							<TextBoxField
								name="firstName"
								label={Localizer('FirstName')}
								testId="careers-form-fisrtname-field"
							/>
							<TextBoxField
								name="lastName"
								label={Localizer('LastName')}
								testId="careers-form-lastname-field"
							/>
							<TextBoxField name="email" label={Localizer('Email')} />
							<TextBoxField name="phone" label={Localizer('Phone')} />
							<DropdownField
								label={Localizer('JobPosition')}
								name="jobPositionId"
								options={jobPositionsOptions}
							/>

							<div>
								<FileUpload
									formApi={form}
									accept="application/pdf,application/msword,application/vnd.openxmlformats-officedocument.wordprocessingml.document,.pdf,.doc,.docx"
									maxSize={1048576} // 1MB
								/>
								<div>{Localizer('FileUploadDisclaimer')}</div>
							</div>

							<ReCaptchaDisclaimer />
							<button
								disabled={submitting}
								type="submit"
								className="roc-btn"
								data-testid="careers-form-submit"
							>
								{Localizer('Apply')} {submitting ? <Spinner light /> : null}
							</button>
						</div>
					</form>
				);
			}}
		/>
	) : (
		<p data-testid="careers-form-submitted">{Localizer('JobApplicationFormSubmitted')}</p>
	);
}

export default JobApplicationForm;
